import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Circle, Lock, Mail, ShieldCheck, Upload, Save, Send } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { financeRules, requiredDocumentCategories } from '../../utils/financeType';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STEP_LABELS = {
  intro: 'Introduction',
  applicant_details: 'Applicant Details',
  business_details: 'Business Details',
  income: 'Income',
  liabilities: 'Liabilities',
  asset_security: 'Asset (Deal Security)',
  documents: 'Document Upload',
  review: 'Review & Submit',
};

function buildSteps(rules) {
  const keys = rules.requiresAsset
    ? ['intro', 'applicant_details', 'business_details', 'income', 'liabilities', 'asset_security', 'documents', 'review']
    : ['intro', 'applicant_details', 'business_details', 'income', 'liabilities', 'documents', 'review'];
  return keys.map((key, idx) => ({ id: idx + 1, key, label: STEP_LABELS[key] }));
}

const DEFAULT_DRAFT = {
  applicant_details: {
    first_name: '',
    last_name: '',
    date_of_birth: '',
    phone: '',
    email: '',
    address: '',
    role: 'director',
  },
  business_details: {
    entity_name: '',
    abn: '',
    acn: '',
    entity_type: '',
    years_trading: '',
    industry: '',
    gst_registered: '',
  },
  finance_request: {
    asset_purpose: '',
    amount_requested: '',
    term_months: '',
    deposit: '',
    balloon: '',
    urgency: '',
    target_settlement: '',
  },
  asset_security: {
    asset_type: '',
    description: '',
    supplier_name: '',
    purchase_price: '',
    deposit_amount: '',
    balloon_percent: '',
    loan_term_months: '',
    new_or_used: '',
    gst_inclusive: '',
    invoice_document_id: '',
  },
  income: {
    business_income: {
      annual_turnover: '',
      net_profit: '',
      director_wages: '',
      other_business_income: '',
    },
    personal_income: {
      salary_wages: '',
      rental_income: '',
      dividends_distributions: '',
      other_income: '',
      spouse_income: '',
    },
    household_expenses: {
      dependants: '',
      monthly_living_expenses: '',
      child_support: '',
      other_commitments: '',
    },
  },
  liabilities: [],
  net_worth: [],
};

function getDocumentOptions(rules) {
  const options = [{ category: 'drivers_licence', label: 'ID / Driver Licence' }];
  if (rules.requiresBankStatements) options.push({ category: 'bank_statements', label: 'Bank Statements' });
  if (rules.requiresAssetQuote) options.push({ category: 'asset_quote', label: 'Invoice / Quote' });
  options.push({ category: 'trust_deed', label: 'Trust Deed (if relevant)' });
  options.push({ category: 'other', label: 'Other Supporting Docs' });
  return options;
}

function hasValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== '';
}

function buildCompleteness(draft, documents, rules, financeType) {
  const requiredDocCategories = requiredDocumentCategories(
    financeType || draft?.finance_type || '',
    draft?.business_details?.entity_type || ''
  );
  const uploadedRequiredCategories = new Set((documents || []).filter(d => d.is_received).map(d => d.category));
  const hasRequiredDocs = requiredDocCategories.every(category => uploadedRequiredCategories.has(category));

  const checks = {
    applicant_details:
      hasValue(draft?.applicant_details?.first_name) &&
      hasValue(draft?.applicant_details?.last_name) &&
      hasValue(draft?.applicant_details?.email) &&
      hasValue(draft?.applicant_details?.phone),
    business_details:
      hasValue(draft?.business_details?.entity_name) &&
      hasValue(draft?.business_details?.abn) &&
      hasValue(draft?.business_details?.entity_type),
    income:
      hasValue(draft?.income?.business_income?.annual_turnover) &&
      hasValue(draft?.income?.business_income?.net_profit) &&
      hasValue(draft?.income?.household_expenses?.monthly_living_expenses),
    liabilities: Array.isArray(draft?.liabilities) && draft.liabilities.length > 0,
    asset_security: !rules.requiresAsset || (
      hasValue(draft?.asset_security?.asset_type) &&
      hasValue(draft?.asset_security?.description) &&
      hasValue(draft?.asset_security?.supplier_name) &&
      hasValue(draft?.asset_security?.purchase_price)
    ),
    documents: hasRequiredDocs,
  };

  const missing = [];
  if (!checks.applicant_details) missing.push('Applicant details incomplete');
  if (!checks.business_details) missing.push('Business details incomplete');
  if (!checks.income) missing.push('Income section incomplete');
  if (!checks.liabilities) missing.push('Liabilities missing');
  if (rules.requiresAsset && !checks.asset_security) missing.push('Asset (deal security) incomplete');
  if (!checks.documents) missing.push('Required documents missing');

  const completeCount = Object.values(checks).filter(Boolean).length;
  return {
    checks,
    missing,
    percent: Math.round((completeCount / Object.keys(checks).length) * 100),
  };
}

export default function ApplicationPortal() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [portalSession, setPortalSession] = useState('');
  const [needsOtp, setNeedsOtp] = useState(true);
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  const [draft, setDraft] = useState(DEFAULT_DRAFT);
  const [documents, setDocuments] = useState([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('drivers_licence');
  const [submitting, setSubmitting] = useState(false);

  const storageKey = `portal_session_${token}`;
  const isReadOnly = !!meta?.is_locked || meta?.status === 'submitted';
  const rules = financeRules(meta?.finance_type || draft?.finance_type || draft?.business_details?.finance_type || 'Asset Finance');
  const steps = useMemo(() => buildSteps(rules), [rules]);
  const documentOptions = useMemo(() => getDocumentOptions(rules), [rules]);

  const completeness = useMemo(() => buildCompleteness(draft, documents, rules, meta?.finance_type), [draft, documents, rules, meta?.finance_type]);

  const authHeaders = portalSession ? { 'X-Portal-Session': portalSession } : {};

  const fetchMeta = async () => {
    try {
      const res = await axios.get(`${API}/crm/portal/${token}`);
      setMeta(res.data);
      if (res.data.contact_email_hint) {
        setEmail(res.data.contact_email_hint);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid application link');
    } finally {
      setLoadingMeta(false);
    }
  };

  const fetchPortalData = async (sessionToken) => {
    try {
      const res = await axios.get(`${API}/portal/applications/${token}/data`, {
        headers: { 'X-Portal-Session': sessionToken },
      });
      setDraft({ ...DEFAULT_DRAFT, ...(res.data.draft || {}) });
      setDocuments(res.data.documents || []);
      setCurrentStep(res.data.current_step || 1);
      setNeedsOtp(false);
    } catch (error) {
      localStorage.removeItem(storageKey);
      setPortalSession('');
      setNeedsOtp(true);
    }
  };

  useEffect(() => {
    fetchMeta();
  }, [token]);

  useEffect(() => {
    const existing = localStorage.getItem(storageKey);
    if (!existing) return;
    setPortalSession(existing);
    fetchPortalData(existing);
  }, [token]);

  useEffect(() => {
    if (currentStep > steps.length) {
      setCurrentStep(steps.length);
    }
  }, [steps.length, currentStep]);

  useEffect(() => {
    if (!documentOptions.find(opt => opt.category === selectedCategory)) {
      setSelectedCategory(documentOptions[0]?.category || 'drivers_licence');
    }
  }, [documentOptions, selectedCategory]);

  const sendOtp = async () => {
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    setSendingOtp(true);
    try {
      await axios.post(`${API}/portal/applications/${token}/otp/send`, { email });
      toast.success('Verification code sent to your email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send code');
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyOtp = async () => {
    if (!email || !otpCode) {
      toast.error('Enter email and OTP code');
      return;
    }
    setVerifyingOtp(true);
    try {
      const res = await axios.post(`${API}/portal/applications/${token}/otp/verify`, { email, code: otpCode });
      setPortalSession(res.data.session_token);
      localStorage.setItem(storageKey, res.data.session_token);
      await fetchPortalData(res.data.session_token);
      toast.success('Portal unlocked');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifyingOtp(false);
    }
  };

  const saveProgress = async (step = currentStep) => {
    if (!portalSession) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/portal/applications/${token}/save`,
        { draft, current_step: step },
        { headers: authHeaders }
      );
      await fetchMeta();
      toast.success('Progress saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  const goNext = async () => {
    const nextStep = Math.min(currentStep + 1, steps.length);
    await saveProgress(nextStep);
    setCurrentStep(nextStep);
  };

  const goBack = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const updateSection = (section, key, value) => {
    setDraft(prev => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [key]: value,
      },
    }));
  };

  const updateIncome = (section, key, value) => {
    setDraft(prev => ({
      ...prev,
      income: {
        ...(prev.income || {}),
        [section]: {
          ...((prev.income || {})[section] || {}),
          [key]: value,
        },
      },
    }));
  };

  const addLiability = () => {
    setDraft(prev => ({
      ...prev,
      liabilities: [
        ...(prev.liabilities || []),
        {
          liability_type: 'other',
          lender: '',
          outstanding_balance: '',
          monthly_repayment: '',
          is_secured: '',
          is_business: '',
          to_be_refinanced: false,
        },
      ],
    }));
  };

  const updateLiability = (index, key, value) => {
    setDraft(prev => {
      const next = [...(prev.liabilities || [])];
      next[index] = { ...next[index], [key]: value };
      return { ...prev, liabilities: next };
    });
  };

  const removeLiability = (index) => {
    setDraft(prev => ({
      ...prev,
      liabilities: (prev.liabilities || []).filter((_, i) => i !== index),
    }));
  };

  const uploadDocument = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', selectedCategory);
      formData.append('name', file.name);

      await axios.post(`${API}/portal/applications/${token}/documents/upload`, formData, {
        headers: {
          ...authHeaders,
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success('Document uploaded');
      await fetchPortalData(portalSession);
      await fetchMeta();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const submitApplication = async () => {
    setSubmitting(true);
    try {
      await saveProgress(currentStep);
      await axios.post(`${API}/portal/applications/${token}/submit`, {}, { headers: authHeaders });
      await fetchMeta();
      await fetchPortalData(portalSession);
      toast.success('Application submitted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingMeta) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center" data-testid="portal-loading">
        Loading application portal...
      </div>
    );
  }

  if (!meta) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-4" data-testid="portal-invalid-link">
        <div className="max-w-md text-center">This application link is invalid or expired.</div>
      </div>
    );
  }

  if (needsOtp) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-4" data-testid="portal-otp-screen">
        <div className="w-full max-w-md bg-slate-900 border border-white/10 rounded-2xl p-6 space-y-4">
          <div className="space-y-1">
            <h1 className="text-xl font-semibold" data-testid="portal-otp-title">Secure Application Access</h1>
            <p className="text-sm text-slate-400" data-testid="portal-otp-subtitle">
              Enter your email and verify with a one-time code to continue.
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-xs text-slate-400">Email address</label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm"
              data-testid="portal-email-input"
            />
          </div>

          <Button
            onClick={sendOtp}
            disabled={sendingOtp}
            className="w-full bg-indigo-600 hover:bg-indigo-700"
            data-testid="portal-send-otp-button"
          >
            <Mail className="h-4 w-4 mr-2" /> {sendingOtp ? 'Sending...' : 'Send Verification Code'}
          </Button>

          <div className="space-y-2">
            <label className="text-xs text-slate-400">Verification code</label>
            <input
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value)}
              className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm tracking-widest"
              data-testid="portal-otp-input"
            />
          </div>

          <Button
            onClick={verifyOtp}
            disabled={verifyingOtp}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            data-testid="portal-verify-otp-button"
          >
            <ShieldCheck className="h-4 w-4 mr-2" /> {verifyingOtp ? 'Verifying...' : 'Verify & Continue'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white px-4 py-6" data-testid="portal-application-screen">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        <aside className="bg-slate-900 border border-white/10 rounded-xl p-4 h-fit sticky top-4" data-testid="portal-sidebar">
          <div className="mb-4">
            <h1 className="text-lg font-semibold" data-testid="portal-client-name">{meta.client_name || 'Application Portal'}</h1>
            <p className="text-xs text-slate-400">{meta.business_name || 'Business application'}</p>
          </div>

          <div className="mb-4" data-testid="portal-progress-block">
            <div className="flex items-center justify-between text-sm mb-1">
              <span>Progress</span>
              <span data-testid="portal-progress-percent">{completeness.percent}%</span>
            </div>
            <div className="h-2 w-full rounded-full bg-white/10">
              <div className="h-2 rounded-full bg-emerald-400" style={{ width: `${completeness.percent}%` }} />
            </div>
          </div>

          <div className="space-y-1" data-testid="portal-step-list">
            {steps.map(step => (
              <button
                key={step.id}
                onClick={() => setCurrentStep(step.id)}
                className={`w-full flex items-center gap-2 text-left px-2 py-2 rounded-lg text-sm ${currentStep === step.id ? 'bg-indigo-500/20 text-indigo-200' : 'text-slate-300 hover:bg-white/5'}`}
                data-testid={`portal-step-nav-${step.key}`}
              >
                {currentStep > step.id ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Circle className="h-4 w-4" />}
                {step.label}
              </button>
            ))}
          </div>

          <div className="mt-5 border-t border-white/10 pt-3" data-testid="portal-missing-summary">
            <p className="text-xs uppercase text-slate-400 mb-2">Missing sections</p>
            {completeness.missing.length ? (
              <ul className="space-y-1 text-xs text-amber-300">
                {completeness.missing.map(item => (
                  <li key={item} data-testid={`portal-missing-${item.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>• {item}</li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-emerald-300" data-testid="portal-missing-none">All key sections complete</p>
            )}
          </div>
        </aside>

        <main className="bg-slate-900 border border-white/10 rounded-xl p-5" data-testid="portal-main-panel">
          {isReadOnly && (
            <div className="mb-4 p-3 rounded-lg border border-amber-400/20 bg-amber-500/10 text-amber-200 text-sm flex items-center gap-2" data-testid="portal-readonly-banner">
              <Lock className="h-4 w-4" /> This application is submitted and read-only. Broker must reopen for edits.
            </div>
          )}

          <StepContent
            stepKey={steps.find(s => s.id === currentStep)?.key}
            draft={draft}
            documents={documents}
            isReadOnly={isReadOnly}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            documentOptions={documentOptions}
            rules={rules}
            updateSection={updateSection}
            updateIncome={updateIncome}
            addLiability={addLiability}
            updateLiability={updateLiability}
            removeLiability={removeLiability}
            uploadDocument={uploadDocument}
            uploading={uploading}
            meta={meta}
          />

          <div className="flex flex-wrap items-center justify-between gap-2 mt-6 pt-4 border-t border-white/10">
            <div className="text-xs text-slate-400" data-testid="portal-footer-status">
              Status: <span className="capitalize">{meta.status?.replace('_', ' ')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => saveProgress(currentStep)}
                disabled={saving || isReadOnly}
                variant="outline"
                className="border-white/20"
                data-testid="portal-save-progress-button"
              >
                <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save Progress'}
              </Button>

              {currentStep > 1 && (
                <Button onClick={goBack} variant="outline" className="border-white/20" data-testid="portal-back-button">
                  Back
                </Button>
              )}

              {currentStep < steps.length && (
                <Button onClick={goNext} disabled={isReadOnly} className="bg-indigo-600 hover:bg-indigo-700" data-testid="portal-next-button">
                  Save & Continue
                </Button>
              )}

              {currentStep === steps.length && (
                <Button
                  onClick={submitApplication}
                  disabled={submitting || isReadOnly}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="portal-submit-button"
                >
                  <Send className="h-4 w-4 mr-1" /> {submitting ? 'Submitting...' : 'Submit Application'}
                </Button>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

function StepContent({
  stepKey,
  draft,
  documents,
  isReadOnly,
  selectedCategory,
  setSelectedCategory,
  documentOptions,
  rules,
  updateSection,
  updateIncome,
  addLiability,
  updateLiability,
  removeLiability,
  uploadDocument,
  uploading,
  meta,
}) {
  if (stepKey === 'intro') {
    return (
      <div className="space-y-4" data-testid="portal-step-intro">
        <h2 className="text-xl font-semibold">Welcome to your application</h2>
        <p className="text-slate-300 text-sm">
          Please complete each section and upload required documents. You can save anytime and resume from this secure link.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <Info label="Client" value={meta.client_name || '—'} />
          <Info label="Business" value={meta.business_name || '—'} />
          <Info label="Finance Type" value={meta.finance_type || '—'} />
          <Info label="Requested Amount" value={meta.amount ? `$${Number(meta.amount).toLocaleString()}` : '—'} />
        </div>
      </div>
    );
  }

  if (stepKey === 'applicant_details') {
    const a = draft.applicant_details || {};
    return (
      <div className="space-y-4" data-testid="portal-step-applicant-details">
        <h2 className="text-lg font-semibold">Applicant Details</h2>
        <FormGrid>
          <Input label="First Name" value={a.first_name} onChange={(v) => updateSection('applicant_details', 'first_name', v)} disabled={isReadOnly} testId="portal-applicant-first-name" />
          <Input label="Last Name" value={a.last_name} onChange={(v) => updateSection('applicant_details', 'last_name', v)} disabled={isReadOnly} testId="portal-applicant-last-name" />
          <Input label="Date of Birth" type="date" value={a.date_of_birth} onChange={(v) => updateSection('applicant_details', 'date_of_birth', v)} disabled={isReadOnly} testId="portal-applicant-dob" />
          <Input label="Phone" value={a.phone} onChange={(v) => updateSection('applicant_details', 'phone', v)} disabled={isReadOnly} testId="portal-applicant-phone" />
          <Input label="Email" type="email" value={a.email} onChange={(v) => updateSection('applicant_details', 'email', v)} disabled={isReadOnly} testId="portal-applicant-email" />
          <Input label="Role in Business" value={a.role} onChange={(v) => updateSection('applicant_details', 'role', v)} disabled={isReadOnly} testId="portal-applicant-role" />
        </FormGrid>
        <Input label="Address" value={a.address} onChange={(v) => updateSection('applicant_details', 'address', v)} disabled={isReadOnly} testId="portal-applicant-address" />
      </div>
    );
  }

  if (stepKey === 'business_details') {
    const b = draft.business_details || {};
    const f = draft.finance_request || {};
    return (
      <div className="space-y-4" data-testid="portal-step-business-details">
        <h2 className="text-lg font-semibold">Business Details</h2>
        <FormGrid>
          <Input label="Entity Name" value={b.entity_name} onChange={(v) => updateSection('business_details', 'entity_name', v)} disabled={isReadOnly} testId="portal-business-entity-name" />
          <Input label="ABN" value={b.abn} onChange={(v) => updateSection('business_details', 'abn', v)} disabled={isReadOnly} testId="portal-business-abn" />
          <Input label="ACN" value={b.acn} onChange={(v) => updateSection('business_details', 'acn', v)} disabled={isReadOnly} testId="portal-business-acn" />
          <Input label="Entity Type" value={b.entity_type} onChange={(v) => updateSection('business_details', 'entity_type', v)} disabled={isReadOnly} testId="portal-business-entity-type" />
          <Input label="Years Trading" value={b.years_trading} onChange={(v) => updateSection('business_details', 'years_trading', v)} disabled={isReadOnly} testId="portal-business-years-trading" />
          <Input label="Industry" value={b.industry} onChange={(v) => updateSection('business_details', 'industry', v)} disabled={isReadOnly} testId="portal-business-industry" />
          <SelectField
            label="GST Registered"
            value={b.gst_registered}
            options={[{ value: '', label: 'Select...' }, { value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]}
            onChange={(v) => updateSection('business_details', 'gst_registered', v)}
            disabled={isReadOnly}
            testId="portal-business-gst-registered"
          />
          <Input label="Requested Amount" type="number" value={f.amount_requested || ''} onChange={(v) => updateSection('finance_request', 'amount_requested', v)} disabled={isReadOnly} testId="portal-finance-amount-requested" />
          <Input label="Term (months)" type="number" value={f.term_months || ''} onChange={(v) => updateSection('finance_request', 'term_months', v)} disabled={isReadOnly} testId="portal-finance-term-months" />
          <Input label="Purpose" value={f.asset_purpose || ''} onChange={(v) => updateSection('finance_request', 'asset_purpose', v)} disabled={isReadOnly} testId="portal-finance-purpose" />
        </FormGrid>
      </div>
    );
  }

  if (stepKey === 'income') {
    const i = draft.income || {};
    return (
      <div className="space-y-5" data-testid="portal-step-income">
        <h2 className="text-lg font-semibold">Income</h2>
        <SubCard title="Business Income">
          <FormGrid>
            <Input label="Annual Turnover" type="number" value={i.business_income?.annual_turnover || ''} onChange={(v) => updateIncome('business_income', 'annual_turnover', v)} disabled={isReadOnly} testId="portal-income-business-turnover" />
            <Input label="Net Profit" type="number" value={i.business_income?.net_profit || ''} onChange={(v) => updateIncome('business_income', 'net_profit', v)} disabled={isReadOnly} testId="portal-income-business-net-profit" />
            <Input label="Director Wages" type="number" value={i.business_income?.director_wages || ''} onChange={(v) => updateIncome('business_income', 'director_wages', v)} disabled={isReadOnly} testId="portal-income-business-director-wages" />
            <Input label="Other Business Income" type="number" value={i.business_income?.other_business_income || ''} onChange={(v) => updateIncome('business_income', 'other_business_income', v)} disabled={isReadOnly} testId="portal-income-business-other" />
          </FormGrid>
        </SubCard>

        <SubCard title="Personal Income">
          <FormGrid>
            <Input label="Salary/Wages" type="number" value={i.personal_income?.salary_wages || ''} onChange={(v) => updateIncome('personal_income', 'salary_wages', v)} disabled={isReadOnly} testId="portal-income-personal-salary" />
            <Input label="Rental Income" type="number" value={i.personal_income?.rental_income || ''} onChange={(v) => updateIncome('personal_income', 'rental_income', v)} disabled={isReadOnly} testId="portal-income-personal-rental" />
            <Input label="Dividends/Distributions" type="number" value={i.personal_income?.dividends_distributions || ''} onChange={(v) => updateIncome('personal_income', 'dividends_distributions', v)} disabled={isReadOnly} testId="portal-income-personal-dividends" />
            <Input label="Other Income" type="number" value={i.personal_income?.other_income || ''} onChange={(v) => updateIncome('personal_income', 'other_income', v)} disabled={isReadOnly} testId="portal-income-personal-other" />
            <Input label="Spouse Income" type="number" value={i.personal_income?.spouse_income || ''} onChange={(v) => updateIncome('personal_income', 'spouse_income', v)} disabled={isReadOnly} testId="portal-income-personal-spouse" />
          </FormGrid>
        </SubCard>

        <SubCard title="Household / Living Expenses">
          <FormGrid>
            <Input label="Dependants" type="number" value={i.household_expenses?.dependants || ''} onChange={(v) => updateIncome('household_expenses', 'dependants', v)} disabled={isReadOnly} testId="portal-income-household-dependants" />
            <Input label="Monthly Living Expenses" type="number" value={i.household_expenses?.monthly_living_expenses || ''} onChange={(v) => updateIncome('household_expenses', 'monthly_living_expenses', v)} disabled={isReadOnly} testId="portal-income-household-living" />
            <Input label="Child Support" type="number" value={i.household_expenses?.child_support || ''} onChange={(v) => updateIncome('household_expenses', 'child_support', v)} disabled={isReadOnly} testId="portal-income-household-child-support" />
            <Input label="Other Commitments" type="number" value={i.household_expenses?.other_commitments || ''} onChange={(v) => updateIncome('household_expenses', 'other_commitments', v)} disabled={isReadOnly} testId="portal-income-household-other" />
          </FormGrid>
        </SubCard>
      </div>
    );
  }

  if (stepKey === 'liabilities') {
    const liabilities = draft.liabilities || [];
    return (
      <div className="space-y-4" data-testid="portal-step-liabilities">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Liabilities</h2>
          <Button onClick={addLiability} disabled={isReadOnly} data-testid="portal-add-liability-button">Add Liability</Button>
        </div>

        {liabilities.length === 0 && (
          <div className="text-sm text-slate-400" data-testid="portal-no-liabilities">No liabilities added yet.</div>
        )}

        <div className="space-y-3">
          {liabilities.map((liability, index) => (
            <div key={index} className="border border-white/10 rounded-lg p-3 space-y-3" data-testid={`portal-liability-row-${index}`}>
              <FormGrid>
                <Input label="Type" value={liability.liability_type || ''} onChange={(v) => updateLiability(index, 'liability_type', v)} disabled={isReadOnly} testId={`portal-liability-type-${index}`} />
                <Input label="Lender" value={liability.lender || ''} onChange={(v) => updateLiability(index, 'lender', v)} disabled={isReadOnly} testId={`portal-liability-lender-${index}`} />
                <Input label="Outstanding Balance" type="number" value={liability.outstanding_balance || ''} onChange={(v) => updateLiability(index, 'outstanding_balance', v)} disabled={isReadOnly} testId={`portal-liability-balance-${index}`} />
                <Input label="Monthly Repayment" type="number" value={liability.monthly_repayment || ''} onChange={(v) => updateLiability(index, 'monthly_repayment', v)} disabled={isReadOnly} testId={`portal-liability-repayment-${index}`} />
                <SelectField
                  label="Secured / Unsecured"
                  value={liability.is_secured}
                  options={[{ value: '', label: 'Select...' }, { value: 'true', label: 'Secured' }, { value: 'false', label: 'Unsecured' }]}
                  onChange={(v) => updateLiability(index, 'is_secured', v)}
                  disabled={isReadOnly}
                  testId={`portal-liability-secured-${index}`}
                />
                <SelectField
                  label="Business / Personal"
                  value={liability.is_business}
                  options={[{ value: '', label: 'Select...' }, { value: 'true', label: 'Business' }, { value: 'false', label: 'Personal' }]}
                  onChange={(v) => updateLiability(index, 'is_business', v)}
                  disabled={isReadOnly}
                  testId={`portal-liability-business-${index}`}
                />
                <SelectField
                  label="To Be Refinanced"
                  value={liability.to_be_refinanced ? 'true' : 'false'}
                  options={[{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }]}
                  onChange={(v) => updateLiability(index, 'to_be_refinanced', v === 'true')}
                  disabled={isReadOnly}
                  testId={`portal-liability-refinanced-${index}`}
                />
              </FormGrid>
              <Button
                variant="outline"
                className="border-red-400/50 text-red-300"
                disabled={isReadOnly}
                onClick={() => removeLiability(index)}
                data-testid={`portal-remove-liability-${index}`}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (stepKey === 'asset_security' && rules.requiresAsset) {
    const a = draft.asset_security || {};
    return (
      <div className="space-y-4" data-testid="portal-step-asset-security">
        <h2 className="text-lg font-semibold">Asset (Deal Security)</h2>
        <FormGrid>
          <Input label="Asset Type" value={a.asset_type || ''} onChange={(v) => updateSection('asset_security', 'asset_type', v)} disabled={isReadOnly} testId="portal-asset-type" />
          <Input label="Description" value={a.description || ''} onChange={(v) => updateSection('asset_security', 'description', v)} disabled={isReadOnly} testId="portal-asset-description" />
          <Input label="Supplier" value={a.supplier_name || ''} onChange={(v) => updateSection('asset_security', 'supplier_name', v)} disabled={isReadOnly} testId="portal-asset-supplier" />
          <Input label="Purchase Price" type="number" value={a.purchase_price || ''} onChange={(v) => updateSection('asset_security', 'purchase_price', v)} disabled={isReadOnly} testId="portal-asset-price" />
          <Input label="Deposit" type="number" value={a.deposit_amount || ''} onChange={(v) => updateSection('asset_security', 'deposit_amount', v)} disabled={isReadOnly} testId="portal-asset-deposit" />
          <Input label="Balloon / Residual (%)" type="number" value={a.balloon_percent || ''} onChange={(v) => updateSection('asset_security', 'balloon_percent', v)} disabled={isReadOnly} testId="portal-asset-balloon" />
          <Input label="Loan Term (months)" type="number" value={a.loan_term_months || ''} onChange={(v) => updateSection('asset_security', 'loan_term_months', v)} disabled={isReadOnly} testId="portal-asset-term" />
          <SelectField
            label="New / Used"
            value={a.new_or_used || ''}
            options={[{ value: '', label: 'Select...' }, { value: 'new', label: 'New' }, { value: 'used', label: 'Used' }, { value: 'demo', label: 'Demo' }]}
            onChange={(v) => updateSection('asset_security', 'new_or_used', v)}
            disabled={isReadOnly}
            testId="portal-asset-new-used"
          />
          <SelectField
            label="GST Inclusive"
            value={a.gst_inclusive || ''}
            options={[{ value: '', label: 'Select...' }, { value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]}
            onChange={(v) => updateSection('asset_security', 'gst_inclusive', v)}
            disabled={isReadOnly}
            testId="portal-asset-gst"
          />
        </FormGrid>
        <p className="text-xs text-slate-400" data-testid="portal-asset-invoice-note">
          Upload invoice/quote in the Documents step using category “Invoice / Quote”.
        </p>
      </div>
    );
  }

  if (stepKey === 'documents') {
    return (
      <div className="space-y-4" data-testid="portal-step-documents">
        <h2 className="text-lg font-semibold">Document Upload</h2>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
          <SelectField
            label="Document Type"
            value={selectedCategory}
            options={documentOptions.map(d => ({ value: d.category, label: d.label }))}
            onChange={setSelectedCategory}
            disabled={isReadOnly}
            testId="portal-document-category"
          />
          <label className={`inline-flex items-center justify-center px-4 py-2 rounded-lg border border-white/20 text-sm ${isReadOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-white/5'}`} data-testid="portal-upload-trigger">
            <Upload className="h-4 w-4 mr-2" /> {uploading ? 'Uploading...' : 'Upload File'}
            <input
              type="file"
              className="hidden"
              disabled={isReadOnly || uploading}
              onChange={(e) => e.target.files?.[0] && uploadDocument(e.target.files[0])}
              data-testid="portal-file-input"
            />
          </label>
        </div>

        <div className="space-y-2" data-testid="portal-uploaded-documents-list">
          {documents.length === 0 ? (
            <div className="text-sm text-slate-400">No documents uploaded yet.</div>
          ) : (
            documents.map(doc => (
              <div key={doc.id} className="flex items-center justify-between border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid={`portal-uploaded-doc-${doc.id}`}>
                <span>{doc.name}</span>
                <span className="text-slate-400 capitalize">{doc.category?.replace(/_/g, ' ')}</span>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="portal-step-review-submit">
      <h2 className="text-lg font-semibold">Review & Submit</h2>
      <p className="text-sm text-slate-300">Please review your details before final submission.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <Info label="Applicant" value={`${draft.applicant_details?.first_name || ''} ${draft.applicant_details?.last_name || ''}`.trim() || '—'} />
        <Info label="Business" value={draft.business_details?.entity_name || '—'} />
        <Info label="Amount" value={draft.finance_request?.amount_requested ? `$${Number(draft.finance_request?.amount_requested).toLocaleString()}` : '—'} />
        <Info label="Liabilities" value={`${(draft.liabilities || []).length} item(s)`} />
        <Info label="Documents" value={`${documents.length} uploaded`} />
      </div>
    </div>
  );
}

function FormGrid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
}

function SubCard({ title, children }) {
  return (
    <div className="border border-white/10 rounded-lg p-3 space-y-3" data-testid={`portal-sub-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <h3 className="font-medium">{title}</h3>
      {children}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="border border-white/10 rounded-lg p-3" data-testid={`portal-info-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <div className="text-xs text-slate-400">{label}</div>
      <div className="text-sm mt-0.5">{value}</div>
    </div>
  );
}

function Input({ label, value, onChange, disabled, testId, type = 'text' }) {
  return (
    <div>
      <label className="text-xs text-slate-400 block mb-1">{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm disabled:opacity-60"
        data-testid={testId}
      />
    </div>
  );
}

function SelectField({ label, value, options, onChange, disabled, testId }) {
  return (
    <div>
      <label className="text-xs text-slate-400 block mb-1">{label}</label>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm disabled:opacity-60"
        data-testid={testId}
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </div>
  );
}
